import React, { useEffect, useMemo, useState } from "react";
import ReactGA from "react-ga4";
import { IconAdjustmentsHorizontal } from "@tabler/icons-react";
import Sidebar from "./components/Sidebar";
import SearchBar from "./components/SearchBar";
import FilterDrawer from "./components/FilterDrawer";
import JobCard from "./components/JobCard";
import { fetchJobs } from "./utils/jobsApi";
import {
  buildLocationOptions,
  buildDepartmentOptions,
  filterJobs,
} from "./utils/jobsUtils";
import {
  EMPTY_STATE_TITLE,
  EMPTY_STATE_SUBTITLE,
  NO_OPENINGS_TITLE,
  NO_OPENINGS_SUBTITLE,
  LOADING_TITLE,
  ERROR_STATE_TITLE,
  ERROR_STATE_SUBTITLE,
} from "./utils/jobsConstants";

/**
 * Job Listings — search + Location/Department filters + job cards.
 *
 * Data source: our internal Careers API ({ jobs: [...] }, served same-origin by
 * the Netlify Function / setupProxy, which owns the Keka Hire integration and
 * token), fetched on mount via `jobsApi`. This component only deals with
 * view-model jobs (see jobsAdapter); jobsApi also serves a localStorage cache
 * fallback if the fetch fails.
 */
const JobListings = () => {
  const [jobs, setJobs] = useState([]);
  // "loading" | "ready" | "error"
  const [status, setStatus] = useState("loading");

  const [search, setSearch] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  // Controls the mobile filter drawer (hidden on desktop, where the sidebar
  // shows the same filters inline).
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setStatus("loading");
    fetchJobs()
      .then((data) => {
        if (cancelled) return;
        setJobs(data);
        setStatus("ready");
        ReactGA.event("careers_jobs_viewed", { job_count: data.length });
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("Failed to load careers jobs:", err);
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Track searches, debounced, so we emit one event when the user stops typing
  // (not one per keystroke) and only for non-empty queries.
  useEffect(() => {
    const term = search.trim();
    if (!term) return undefined;
    const timer = setTimeout(
      () => ReactGA.event("careers_job_search", { search_term: term }),
      800
    );
    return () => clearTimeout(timer);
  }, [search]);

  // Faceted counts: each filter group's options/counts reflect the OTHER active
  // filters (search + the other group), but NOT its own selection — so counts
  // update dynamically as you filter while multi-select stays usable.
  const locationOptions = useMemo(
    () =>
      buildLocationOptions(
        filterJobs(jobs, {
          search,
          departments: selectedDepartments,
          locations: [],
        }),
        selectedLocations
      ),
    [jobs, search, selectedDepartments, selectedLocations]
  );
  const departmentOptions = useMemo(
    () =>
      buildDepartmentOptions(
        filterJobs(jobs, {
          search,
          locations: selectedLocations,
          departments: [],
        }),
        selectedDepartments
      ),
    [jobs, search, selectedLocations, selectedDepartments]
  );

  const filteredJobs = useMemo(
    () =>
      filterJobs(jobs, {
        search,
        locations: selectedLocations,
        departments: selectedDepartments,
      }),
    [jobs, search, selectedLocations, selectedDepartments]
  );

  const toggleValue = (setter) => (value) =>
    setter((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );

  const hasActiveFilters =
    search.trim() !== "" ||
    selectedLocations.length > 0 ||
    selectedDepartments.length > 0;

  const handleReset = () => {
    setSearch("");
    setSelectedLocations([]);
    setSelectedDepartments([]);
  };

  const renderListings = () => {
    if (status === "loading") {
      return (
        <div className="jobsEmptyState">
          <p className="jobsEmptyTitle">{LOADING_TITLE}</p>
        </div>
      );
    }

    if (status === "error") {
      return (
        <div className="jobsEmptyState">
          <p className="jobsEmptyTitle">{ERROR_STATE_TITLE}</p>
          <p className="jobsEmptySubtitle">{ERROR_STATE_SUBTITLE}</p>
        </div>
      );
    }

    if (filteredJobs.length === 0) {
      // Distinguish "no roles exist at all" from "filters/search hid them all".
      const noJobsAtAll = jobs.length === 0;
      return (
        <div className="jobsEmptyState">
          <p className="jobsEmptyTitle">
            {noJobsAtAll ? NO_OPENINGS_TITLE : EMPTY_STATE_TITLE}
          </p>
          <p className="jobsEmptySubtitle">
            {noJobsAtAll ? NO_OPENINGS_SUBTITLE : EMPTY_STATE_SUBTITLE}
          </p>
        </div>
      );
    }

    return filteredJobs.map((job) => <JobCard key={job.id} job={job} />);
  };

  // Wrap the toggles to emit a filter event only when a value is newly selected
  // (not on deselect), to avoid noise.
  const toggleLocation = (value) => {
    if (!selectedLocations.includes(value)) {
      ReactGA.event("careers_job_filter", {
        filter_type: "location",
        filter_value: value,
      });
    }
    toggleValue(setSelectedLocations)(value);
  };
  const toggleDepartment = (value) => {
    if (!selectedDepartments.includes(value)) {
      ReactGA.event("careers_job_filter", {
        filter_type: "department",
        filter_value: value,
      });
    }
    toggleValue(setSelectedDepartments)(value);
  };

  return (
    <>
      <div className="jobsContent">
        <Sidebar
          search={search}
          onSearchChange={setSearch}
          locationOptions={locationOptions}
          selectedLocations={selectedLocations}
          onToggleLocation={toggleLocation}
          departmentOptions={departmentOptions}
          selectedDepartments={selectedDepartments}
          onToggleDepartment={toggleDepartment}
          hasActiveFilters={hasActiveFilters}
          onReset={handleReset}
        />

        <div className="jobsMain">
          {/* Search + filter toggle — shown only on mobile (see CSS). */}
          <div className="jobsMobileControls">
            <SearchBar value={search} onChange={setSearch} />
            <button
              type="button"
              className="jobsFilterToggle"
              onClick={() => setIsFilterOpen(true)}
              aria-label="Open filters"
            >
              <IconAdjustmentsHorizontal size={20} stroke={2} />
            </button>
          </div>

          <div className="jobsListings">{renderListings()}</div>
        </div>
      </div>

      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        locationOptions={locationOptions}
        selectedLocations={selectedLocations}
        onToggleLocation={toggleLocation}
        departmentOptions={departmentOptions}
        selectedDepartments={selectedDepartments}
        onToggleDepartment={toggleDepartment}
        hasActiveFilters={hasActiveFilters}
        onReset={handleReset}
      />
    </>
  );
};

export default JobListings;
